import { useId, type InputHTMLAttributes, type ReactNode } from "react";
import { FieldMarker } from "@/components/FieldMarker";

// Labeled text input with optional inline error, shared across the auth forms
// (DRY). Full-width and stacked for the mobile-first layout. The error is wired
// to the input via aria-describedby + aria-invalid for accessibility.
interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  /** Show a muted "(optional)" marker (the counterpart to the required "*"). */
  optional?: boolean;
  /**
   * A unit rendered INSIDE the control's box, before the editable text — the
   * `$` on a currency field (BRK-S142). Named `adornStart` rather than `prefix`
   * because `prefix` is a real (RDFa) HTML attribute on every element, so the
   * obvious name collides with what this interface extends.
   *
   * Part of the box rather than of the value on purpose: baked into the text a
   * broker can select and delete it, and every parse then has to strip a
   * character the field itself put there. `aria-hidden`, because the label
   * already says what the field is and a screen reader announcing "dollar sign
   * Bond penalty" reads worse than the label alone.
   */
  adornStart?: ReactNode;
  /** The same, after the editable text — the `%` on a percent field. */
  adornEnd?: ReactNode;
  /**
   * An INTERACTIVE control drawn inside the box after the editable text — the
   * bond penalty's % / $ toggle (BRK-S181). Unlike the two adornments it is
   * not `aria-hidden`: it is a control, and hiding it would take it out of the
   * accessibility tree. Suppresses nothing by itself; a caller that makes it
   * the unit indicator passes no adornment beside it.
   */
  endControl?: ReactNode;
}

export function TextField({
  label,
  error,
  id,
  optional,
  adornStart,
  adornEnd,
  endControl,
  ...inputProps
}: TextFieldProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const errorId = `${fieldId}-error`;
  const adorned =
    adornStart !== undefined ||
    adornEnd !== undefined ||
    endControl !== undefined;

  const input = (
    <input
      id={fieldId}
      className={
        adorned
          ? // The BOX is the wrapper when there is an adornment, so the input
            // itself carries no border and no ring: two nested focus rings on
            // one control is the visual bug this avoids.
            "w-full min-w-0 bg-transparent py-2 text-brk-text outline-none"
          : "w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
      }
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? errorId : undefined}
      {...inputProps}
    />
  );

  return (
    <div className="flex w-full flex-col gap-1.5 text-left">
      <span className="flex items-baseline gap-1.5">
        <label htmlFor={fieldId} className="text-sm font-medium text-brk-text">
          {label}
        </label>
        <FieldMarker required={inputProps.required} optional={optional} />
      </span>
      {adorned ? (
        <span className="flex w-full items-center gap-1.5 rounded-md border border-brk-border bg-brk-panel px-3 text-brk-dim focus-within:border-brk-accent focus-within:ring-2 focus-within:ring-brk-accent/30">
          {adornStart !== undefined && (
            <span aria-hidden="true" className="shrink-0 text-sm">
              {adornStart}
            </span>
          )}
          {input}
          {adornEnd !== undefined && (
            <span aria-hidden="true" className="shrink-0 text-sm">
              {adornEnd}
            </span>
          )}
          {endControl !== undefined && (
            // Pulled to the box's right edge, so a segmented control sits
            // flush inside the border rather than floating in its padding.
            <span className="-mr-2 flex shrink-0 items-center">
              {endControl}
            </span>
          )}
        </span>
      ) : (
        input
      )}
      {error && (
        <p id={errorId} className="text-sm text-brk-danger">
          {error}
        </p>
      )}
    </div>
  );
}
